import { useEffect, useRef, useState, useMemo } from "react";
import { allowOrWarn, Util } from '../../util/Util';
import { useAuth } from "../../hooks/useAuth";
import { useData } from "../../hooks/useData";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import TableView from "../../components/TableView";
import { AddCircleOutline, Delete, Edit, Print, Refresh } from "@mui/icons-material";
import { useDateRange } from "../../hooks/useDateRange";
import { useSessionState } from "../../hooks/useSessionState";
import { MRT_ColumnDef } from "material-react-table";
import ExportMenuButton from "../../components/ExportMenuButton";
import StockTraceDialog from "./StockTraceDialog";
import InvoiceReport from "../invoice/InvoiceReport";
import MovementModal from "./MovementModal";
import SupplyReport from "../supply/SupplyReport";
import ConversionModal from "../conversion/ConversionModal";
import { ConversionProvider } from "../conversion/ConversionContext";
import {
	hasStockTraceReference,
	resolveStockTraceModalKind,
	stockTraceModalGrant,
	toStockTraceModalBundle,
	type StockTraceModalKind,
} from "./stockTraceReferenceModal";
import { createSignCell } from "../../util/cellFormatters";
import { parseISODay } from "../../util/businessClock";

import { roundQty } from "../../util/quantity";
import { allows, canAmend, canDrop, canSeeStockTimeline } from '../../util/access';
const MovementTracingChrono = () => {
	const [toEdit, setToEdit] = useState<any>(null);
	const [toDelete, setToDelete] = useState<any>(null);
	const [traceView, setTraceView] = useState<{ kind: StockTraceModalKind; bundle: any } | null>(null);
	// Kept for the session so Refresh still works after leaving the page and coming back.
	const [filters, setFilters] = useSessionState<any>(`trace:${LocalStorageKeys.MOVEMENT_TRACE_CHRONO_RECORDS}`, {});
	const restoredFiltersRef = useRef(Object.keys(filters).length !== 0);
	const [newTrace, setNewTrace] = useState<boolean>(false);

	const { api, session, access } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading } = useData(LocalStorageKeys.MOVEMENT_TRACE_CHRONO_RECORDS);
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(LocalStorageKeys.MOVEMENT_TRACE_CHRONO_RECORDS);

	const toastIdRef = useRef<string | number>(-1);
	const firstFetchRef = useRef(false);

	const handleReferenceClick = (row: any) => {
		if (!hasStockTraceReference(row)) return;
		const kind = resolveStockTraceModalKind(row);
		// The trace lists every movement; reading the document behind one is
		// still the user's own permission, and its data scope on the backend.
		if (!allowOrWarn(allows({ access }, stockTraceModalGrant(kind)))) {
			return;
		}
		setTraceView({ kind, bundle: toStockTraceModalBundle(row) });
	};

	const closeTraceView = () => setTraceView(null);

	useEffect(() => {
		loadFromIndexedDB();
	}, []);

	const fetchData = async () => {
		setLoading(true);

		try {
			const res = await api.get(`movements/trace/chrono`, {
				params: {
					...filters
				}
			});

			if (res) {
				if (res.status === 200) {
					// The period of the filters just sent, not the state from the render before they changed.
					saveFromServer(res.data, filters.From ? { from: new Date(filters.From), to: new Date(filters.To) } : period, true);
					toast.update(toastIdRef.current, {
						render: t("common:toast.success.refresh"),
						type: "success",
						isLoading: false,
						autoClose: 3000,
					});
				} else {
					console.error("Failed to fetch data:", res);
					toast.dismiss(toastIdRef.current);
				}
			} else {
				toast.dismiss(toastIdRef.current);
			}
		} finally {
			setLoading(false);
		}
	};

	const exportFilteredReport = async () => {
		if (Object.keys(filters).length === 0) return;

		setLoadingMessage("EXPORT");

		const res = await api.get("/reports/stock/movement", {
			params: filters,
		});

		if (res) {
			if (res.status === 200) {
				const pdfData = atob(res.data.data);
				const blob = new Blob([Uint8Array.from(pdfData, c => c.charCodeAt(0))], { type: "application/pdf" });
				window.open(URL.createObjectURL(blob));
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setLoadingMessage(null);
	};

	useEffect(() => {
		// Restored filters: the cached rows already answer them; Refresh asks again.
		if (restoredFiltersRef.current) {
			restoredFiltersRef.current = false;
			return;
		}
		if (Object.keys(filters).length !== 0) {
			setPeriod({ from: new Date(filters.From), to: new Date(filters.To) });
			fetchData();
		}
	}, [filters])

	// Opening rows render an em dash instead of a quantity, movements the signed value.
	const QuantityCell = createSignCell({ treatZeroAs: "neutral" });

	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.date"),
			id: "date",
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"), //render Date as a string
			filterVariant: 'date-range',
		},
		{
			header: t("common:tables.sector"),
			accessorKey: "sector",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.category"),
			accessorKey: "category",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.itemCode"),
			accessorKey: "item_code",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.item"),
			accessorKey: "item_name",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.quantity"),
			accessorKey: "quantity",
			// The opening row carries a balance, not a movement: it has no
			// quantity of its own, and 0 would read as "nothing moved".
			Cell: (props: any) => props.row.original?.is_opening
				? <>&mdash;</>
				: <QuantityCell cell={props.cell} />,
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.quantity);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.runningStock"),
			accessorKey: "running_stock",
			Cell: createSignCell({ showPlusPrefix: false, treatZeroAs: "neutral" }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.operation"),
			accessorKey: "operation",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.sourceDepot"),
			accessorKey: "source",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.targetDepot"),
			accessorKey: "destination",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.reference"),
			accessorKey: "reference",
			Cell: ({ cell }: { cell: any }) => cell.getValue() ? (
				<button
					type="button"
					style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
					onClick={() => handleReferenceClick(cell.row.original)}
				>
					{cell.getValue()}
				</button>
			) : null,
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.observerReceiver"),
			accessorKey: "client",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.user"),
			accessorKey: "user",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		}
	], [t]);

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
					stateKey={LocalStorageKeys.MOVEMENT_TRACE_CHRONO_RECORDS}
					header={t("common:movement.headers.chronologicalTracing")}
					data={data}
					columns={columns}
					totals={[{ key: "quantity", label: t("common:tables.quantity") }]}
					lastUpdated={lastUpdate}
					useDateRangeFilter={false}
					period={dateRange}

					isLoading={loading}
					enableRowActions={false}
					getRowSx={(row) => row.original?.is_opening ? { fontStyle: 'italic' } : {}}
					hideColumns={["sector", "category", "item_code"]}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.buttons.printReport")}>
								<IconButton
									onClick={() => {
										if (canSeeStockTimeline(access)) {
											exportFilteredReport();
										}
									}}
									color="primary"
									disabled={Object.keys(filters).length === 0}>
									<Print />
								</IconButton>
							</Tooltip>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary" disabled={Object.keys(filters).length === 0}>
									<Refresh />
								</IconButton>
							</Tooltip>
							<Tooltip title={t("common:ui.tooltips.newStockTracing")}>
								<IconButton
									onClick={() => {
										if (canSeeStockTimeline(access)) {
											setNewTrace(true);
										}
									}}
									color="success">
									<AddCircleOutline />
								</IconButton>
							</Tooltip>
							<ExportMenuButton
								prefix="movement_tracing"
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => [
						!(toDelete || toEdit) ? (
							<MenuItem
								key={0}
								onClick={() => {
									if (allowOrWarn(canAmend(access, 'movement'))) {
										setToEdit(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'primary.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'primary.main',
								}}>
									<Edit />
								</ListItemIcon>
								{t("common:ui.buttons.edit")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit) ? (
							<MenuItem
								key={1}
								onClick={() => {
									if (allowOrWarn(canDrop(access, 'movement'))) {
										setToDelete(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'error.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'error.main',
								}}>
									<Delete />
								</ListItemIcon>
								{t("common:ui.buttons.delete")}
							</MenuItem>
						) : null,
					]}
				/>

			</LocalizationProvider>


			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<StockTraceDialog
					open={newTrace}
					onClose={() => setNewTrace(false)}
					setFilters={setFilters}
				/>
			}


			{traceView?.kind === "sale" && (
				<InvoiceReport
					isOpen
					toggle={closeTraceView}
					bundle={traceView.bundle}
				/>
			)}

			{traceView?.kind === "movement" && (
				<MovementModal
					isOpen
					toggle={closeTraceView}
					bundle={traceView.bundle}
				/>
			)}

			{traceView?.kind === "supply" && (
				<SupplyReport
					isOpen
					toggle={closeTraceView}
					bundle={traceView.bundle}
				/>
			)}

			{traceView?.kind === "conversion" && (
				<ConversionProvider>
					<ConversionModal
						isOpen
						toggle={closeTraceView}
						bundle={traceView.bundle}
					/>
				</ConversionProvider>
			)}
		</>
	);
};

export default MovementTracingChrono;
